/**
 * Cloudflare Turnstile — the ONE implementation, shared by every gated form.
 *
 * WHY THIS FILE EXISTS. apply-form, invite-form, contact-form and notify-form
 * each carried a private copy of `#turnstileRequired()` / `#turnstileToken()`,
 * and the copies had already drifted: two announced "Verifying you are human…"
 * during the wait and two sat silent, with nothing recording that as a choice.
 * One behaviour now has one home. `assets/utils.js` was removed because nothing
 * imported it; this module exists because four modules do.
 *
 * TURNSTILE FAILS CLOSED. The Worker answers 403 to a missing or unsolved
 * token, so anything here that returns early without a token breaks a live
 * funnel. That is why the wait is generous (12s) and why the token is read from
 * the DOM rather than from a callback we could miss.
 *
 * TWO LOADING MODES, chosen in snippets/turnstile.liquid and never here:
 *
 *   EAGER  `.cf-turnstile`       The snippet also emits Cloudflare's api.js, so
 *                                the script auto-renders (implicit mode) at
 *                                page load and the token is normally already in
 *                                hand before the visitor presses submit. Used
 *                                by apply, invite, contact and the notify BLOCK
 *                                — every form where the visitor is committed
 *                                and a wait is worse than a preload.
 *
 *   LAZY   `.cf-turnstile-lazy`  No script tag at all. The 26.5KB of
 *                                third-party JS is fetched on the visitor's
 *                                FIRST INTERACTION with the form and the widget
 *                                is then rendered explicitly. Used only by the
 *                                footer newsletter, which is on all seven routes
 *                                and was making every page pay for Turnstile to
 *                                protect one email field.
 *
 * THE LAZY CLASS IS DELIBERATELY NOT `cf-turnstile`. Implicit mode renders
 * EVERY `.cf-turnstile` on the page, so on a route that has both an eager form
 * and the footer (contact, apply, invite) Cloudflare's own script would render
 * the footer widget as well, and our explicit render would then be a second
 * render of the same element. A different class makes that collision
 * impossible, and `turnstileRequired()` below matches both so a form can never
 * decide bot protection is absent just because its widget has not booted yet.
 */

const API_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/** Global Cloudflare invokes once api.js has finished evaluating. */
const READY_CALLBACK = 'yuuthTurnstileReady';

/** How long a submit will wait for a token before giving up. */
const WAIT_MS = 12000;

/** How often the hidden response input is re-read while waiting. */
const POLL_MS = 250;

/** @type {Promise<void> | null} */
let apiReady = null;

/**
 * Cloudflare's runtime. Untyped on purpose: there is no @types package in a
 * theme with no build step, and one cast here is cheaper than a fake interface.
 * @returns {any}
 */
function api() {
  return /** @type {any} */ (window).turnstile;
}

/**
 * Load api.js at most once per page, whichever mode asked for it first.
 *
 * Three cases, in order: already evaluated (eager widget on this route, or a
 * second lazy form); a script tag present but still in flight, which we wait on
 * rather than duplicating; nothing at all, which is the only case that injects.
 * `render=explicit` suppresses auto-render for the tag WE add — an eager tag on
 * the same page keeps its own implicit behaviour, because that is a property of
 * its own URL, not a global mode.
 * @returns {Promise<void>}
 */
function loadApi() {
  if (apiReady) return apiReady;
  apiReady = new Promise((resolve) => {
    if (api()) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src^="' + API_SRC + '"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    /** @type {any} */ (window)[READY_CALLBACK] = resolve;
    const script = document.createElement('script');
    script.src = API_SRC + '?render=explicit&onload=' + READY_CALLBACK;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });
  return apiReady;
}

/**
 * Start a lazy widget: fetch Cloudflare's script, then render this one element.
 *
 * Idempotent, so it is safe to wire to several interaction events, and a no-op
 * when the form's widget is eager (Cloudflare already rendered that one) or
 * when no site key is configured (the snippet then emits nothing at all).
 * @param {ParentNode} root the form's custom element
 */
export function bootTurnstile(root) {
  const el = /** @type {HTMLElement | null} */ (root.querySelector('.cf-turnstile-lazy'));
  if (!el || el.dataset['booted']) return;
  el.dataset['booted'] = 'true';
  void loadApi().then(() => {
    api()?.render(el, {
      sitekey: el.dataset['sitekey'],
      appearance: el.dataset['appearance'] || 'interaction-only',
    });
  });
}

/**
 * Whether bot protection is configured for this form. False only when the
 * snippet rendered nothing, which matches the Worker skipping verification when
 * it holds no secret.
 * @param {ParentNode} root
 * @returns {boolean}
 */
export function turnstileRequired(root) {
  return Boolean(root.querySelector('.cf-turnstile, .cf-turnstile-lazy'));
}

/**
 * The solved token, or '' if none arrives in time.
 *
 * Cloudflare fills a hidden `cf-turnstile-response` input asynchronously, so
 * reading it the instant a form submits usually finds it empty. Booting first
 * covers the lazy case where the visitor somehow reached submit without ever
 * focusing a field (browser autofill, a restored form), and the poll then
 * covers both the script load and an interactive challenge.
 * @param {ParentNode} root
 * @param {() => void} [onWait] Announced ONCE, and only when there is a real
 *   pause to explain — never on the common path where the token is already in.
 * @returns {Promise<string>}
 */
export async function turnstileToken(root, onWait) {
  if (!turnstileRequired(root)) return '';
  bootTurnstile(root);
  const read = () => {
    const el = /** @type {HTMLInputElement | null} */ (
      root.querySelector('[name="cf-turnstile-response"]')
    );
    return el?.value ?? '';
  };
  let token = read();
  if (token) return token;
  onWait?.();
  const deadline = Date.now() + WAIT_MS;
  while (!token && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    token = read();
  }
  return token;
}
